/**
 * THE WEB APP'S ANALYTICS (#708; ADR 0018; `docs/superpowers/specs/2026-10-13-analytics-gtm-ga4-design.md`), DARK
 * WITHOUT AN ID: with no `VITE_GTM_CONTAINER_ID` in the build (`config.ts`), or off the two production hosts, nothing is
 * asked and nothing loads. Production's build has one since the turn-on (#713). This file is the module's whole public face; nothing outside `frontend/src/analytics/` imports
 * any other file of it.
 *
 *   config.ts     the container id, the hosts, the text version, the cookie's contract
 *   consent.ts    the cookie, and the reconciliation of the browser's answer with the account's, as a table
 *   scrub.ts      the page fields: route templates, referrers, titles
 *   campaigns.ts  the campaign convention and its check
 *   events.ts     the allowlist and `track`
 *   loader.ts     the decision applied: Consent Mode, the container, the queue, withdrawal, sign-out
 *   account.ts    what the module reads of `GET /api/me`, and the `web_visitor` record
 *
 * ── THE API ─────────────────────────────────────────────────────────────────────────────────────────
 *
 * BOOT (main.tsx, after `consumeTokenFromUrl()` and `dropLegacyWebToken()`, before the first render):
 *   startAnalytics({ router, queryClient })   page views on every route change, the account from the `['me']` cache
 *
 * EVENTS (#708's second task):
 *   track(event, params?)    typed by `TrackedEvents` (events.ts). Sent only with a grant; queued on a credential
 *                            route for the same page life; dropped otherwise. `page_view` is the module's own.
 *                            `consent_changed` for the analytics consent is sent by `answerAnalytics('g')` itself;
 *                            callers report the AI consent's changes only.
 *   signOutAnalytics()       after `signOut()` resolved, before leaving the page. Returns `{ fullPageLoad }`: when
 *                            true, end with `window.location.assign('/login')`, not `navigate('/login')`.
 *
 * THE ASK, SETTINGS AND THE CONFLICT (#709, CO-D4):
 *   useAnalyticsState()      the state to draw from (and getAnalyticsState / subscribeAnalytics outside React):
 *     mode 'off'        nothing is asked and nothing loads (no container id, not a production host, a text the
 *                       server does not hold in force); `reason` says which
 *     mode 'pending'    signed in, `GET /api/me` has not answered: draw nothing yet
 *     mode 'ask'        no answer to the current text on either side: loads nothing. Asked only when `changed` is
 *                       false, i.e. no answer of any version exists (first run's step, the visitor's band; CO-D1).
 *                       `changed` is true when either side holds an answer to an older text: that is an answer,
 *                       and nothing asks unprompted (CO-D4)
 *     mode 'granted'    loads. `browser` holds the answer's date and version ("All accepted {date} · Version {n}")
 *     mode 'declined'   loads nothing. `browser` holds the date ("You chose only functional cookies on {date}.")
 *     mode 'conflict'   loads nothing and asks nothing (CO-D4): a no wins. `conflict.browser` (choice, seconds) and
 *                       `conflict.account` (choice, status, ISO date) say what disagreed
 *     and on every state: `browser` (the cookie's answer, any version), `account` (`me.consents.analytics`),
 *     `signedIn`, `withdrawnNow` (this page is the one a withdrawal reloaded: say `withdrawnNow` once).
 *   answerAnalytics(choice)  'g' or 'd'. A visitor: on the press. Signed in: after `useAnswerConsent` stored the
 *                            answer (client `web`), never before. Writes the cookie and reads it back; returns
 *                            `{ kept }`, false when the browser keeps no cookie (AA-D9's state). A yes loads the
 *                            container; a no on a page that loaded it is a withdrawal. Call it in the same tick the
 *                            mutation resolves: until then a yes stored over this browser's no reads as `conflict`
 *                            (an account that moves to a yes is never taken by the browser on its own, R47).
 *   withdrawAnalytics()      signed in: after `useWithdrawConsent` stored it. The cookie becomes `d`, Google's cookies
 *                            go, the page reloads. Returns `{ kept }`; a visitor's browser that kept no `d` is not
 *                            reloaded (the old yes would come back) and stops in place.
 *   The answer the account takes from the cookie at sign-in (the table's rows 4 and 5) is recorded by this module
 *   (client `web_visitor`, ruling R3); a screen never sends it.
 */
import type { QueryClient } from '@tanstack/react-query'
import { useSyncExternalStore } from 'react'

import { canonicalPageUrl } from '../api/canonicalHost'
import { readAccount, recordVisitorAnswer, refreshAccount, watchAccount } from './account'
import { GTM_CONTAINER_ID, WITHDRAWN_MARKER } from './config'
import type { Choice } from './consent'
import { setSink } from './events'
import { NOT_STARTED, createAnalytics, type AnalyticsController, type AnalyticsState, type LoaderDocument, type LoaderWindow, type PageLocation } from './loader'

export { track } from './events'
export type { CardType, FirstRunStep, SettingsArea, TrackedEvent, TrackedEvents } from './events'
export type { AnalyticsState } from './loader'
export type { Answer, Choice } from './consent'

/** What of the data router the module needs: its location now, and its changes. */
export interface RouterLike {
  state: { location: PageLocation }
  subscribe(listener: (state: { location: PageLocation }) => void): () => void
}

let controller: AnalyticsController | null = null

/**
 * Boot. Once per page life; a second call does nothing. Returns what undoes the wiring (the listeners, the
 * subscriptions, the controller), which a page life never needs and a test does.
 */
export function startAnalytics({ router, queryClient }: { router: RouterLike; queryClient: QueryClient }): () => void {
  if (controller !== null) return () => {}
  const where = window.location
  // A page on the wrong host is about to move (OnCanonicalHost): the page it moves to is measured, not this one.
  if (canonicalPageUrl({ host: where.host, pathname: where.pathname, search: where.search }) !== null) return () => {}
  const justWithdrawn = where.hash === WITHDRAWN_MARKER
  if (justWithdrawn) window.history.replaceState(window.history.state, '', where.pathname + where.search)

  const started = createAnalytics({
    containerId: GTM_CONTAINER_ID,
    hostname: where.hostname,
    referrer: document.referrer,
    win: window as unknown as LoaderWindow,
    doc: document as unknown as LoaderDocument,
    now: () => Date.now(),
    account: () => readAccount(queryClient),
    record: (answer) => recordVisitorAnswer(queryClient, answer),
    refreshMe: () => refreshAccount(queryClient),
    reloadWithdrawn: () => {
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search + WITHDRAWN_MARKER)
      window.location.reload()
    },
    justWithdrawn,
  })
  controller = started
  started.start({ pathname: where.pathname, search: where.search, hash: where.hash })

  const unroute = router.subscribe((state) => started.route(state.location))
  const unwatch = watchAccount(queryClient, () => started.refresh())
  const recheck = () => started.refresh()
  const shown = () => {
    if (document.visibilityState === 'visible') recheck()
  }
  window.addEventListener('pageshow', recheck)
  window.addEventListener('focus', recheck)
  document.addEventListener('visibilitychange', shown)
  return () => {
    unroute()
    unwatch()
    window.removeEventListener('pageshow', recheck)
    window.removeEventListener('focus', recheck)
    document.removeEventListener('visibilitychange', shown)
    setSink(undefined)
    controller = null
  }
}

export function getAnalyticsState(): AnalyticsState {
  return controller?.getState() ?? NOT_STARTED
}

export function subscribeAnalytics(listener: () => void): () => void {
  return controller?.subscribe(listener) ?? (() => {})
}

/** The analytics state, for a screen. */
export function useAnalyticsState(): AnalyticsState {
  return useSyncExternalStore(subscribeAnalytics, getAnalyticsState)
}

export function answerAnalytics(choice: Choice): { kept: boolean } {
  return controller?.answer(choice) ?? { kept: false }
}

export function withdrawAnalytics(): { kept: boolean } {
  return { kept: controller?.withdraw() ?? false }
}

export function signOutAnalytics(): { fullPageLoad: boolean } {
  return controller?.signOut() ?? { fullPageLoad: false }
}
